import { CompanyProfile } from '../types/auth';
import { Project } from '../types/project';

// The Preparer Information block on the Project Info tab is owned by the user's
// Company Profile (Account Settings → Company Profile). Every field there is
// seeded from the profile so the section arrives pre-filled; the inputs stay
// editable, and edits are per-project — they never write back to the profile.

/** The four address parts, held identically by a Company Profile and a project. */
export interface CompanyAddressParts {
  company_address?: string | null;
  company_city?: string | null;
  company_state?: string | null;
  company_zip?: string | null;
}

/**
 * Address flattened to the single line the report header prints.
 *
 * Takes the parts rather than a profile so the same formatting serves both sides.
 * A project created before the address split holds its whole flattened address in
 * `company_address` with the other three empty, and this returns that line
 * unchanged — which is why no backfill was needed.
 */
export function formatCompanyAddress(parts: CompanyAddressParts): string {
  if (!parts.company_address) return '';
  const cityStateZip = `${parts.company_city || ''}, ${parts.company_state || ''} ${parts.company_zip || ''}`
    .replace(/^,\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/,$/, '');
  return cityStateZip ? `${parts.company_address}, ${cityStateZip}` : parts.company_address;
}

/**
 * Every preparer field the Company Profile supplies. Report date is separate.
 *
 * The address carries across part-for-part rather than flattened, so the project
 * can push its values back to the profile without cramming a whole address into
 * the street field. Flattening happens once, at the report.
 */
export function preparerFieldsFromProfile(profile: CompanyProfile): Partial<Project> {
  return {
    prepared_by: profile.preparer_name || '',
    preparer_title: profile.preparer_title || '',
    preparer_license: profile.preparer_license || '',
    preparer_email: profile.company_email || '',
    company_name: profile.company_name || '',
    company_address: profile.company_address || '',
    company_city: profile.company_city || '',
    company_state: profile.company_state || '',
    company_zip: profile.company_zip || '',
  };
}

/** The reverse map: project preparer fields written back to the Company Profile. */
export function profileFieldsFromProject(project: Partial<Project>): Partial<CompanyProfile> {
  return {
    company_name: project.company_name || '',
    preparer_name: project.prepared_by || '',
    preparer_title: project.preparer_title || '',
    preparer_license: project.preparer_license || '',
    company_email: project.preparer_email || '',
    company_address: project.company_address || '',
    company_city: project.company_city || '',
    company_state: project.company_state || '',
    company_zip: project.company_zip || '',
  };
}

/**
 * True when the profile holds none of the fields a project would seed from it —
 * the "never set one up" state. The save-to-profile offer appears only here, so
 * using it once retires it and a partially-filled profile is never nagged.
 */
export function isProfileEmpty(profile: CompanyProfile | null): boolean {
  if (!profile) return true;
  return ![
    profile.company_name,
    profile.preparer_name,
    profile.preparer_title,
    profile.preparer_license,
    profile.company_email,
    profile.company_address,
  ].some(v => v?.trim());
}

/** ISO date (yyyy-mm-dd) in the browser's local timezone, for `<input type="date">`. */
export function toDateInputValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().split('T')[0];
}

/**
 * Preparer defaults for a project: profile fields plus a report date defaulted
 * to the project's creation date. `createdAt` is omitted for a project being
 * created right now, in which case today is the creation date.
 */
export function preparerDefaults(profile: CompanyProfile | null, createdAt?: string | null): Partial<Project> {
  const created = createdAt ? new Date(createdAt) : new Date();
  const report_date = Number.isNaN(created.getTime()) ? toDateInputValue(new Date()) : toDateInputValue(created);
  return { ...(profile ? preparerFieldsFromProfile(profile) : {}), report_date };
}

/**
 * The subset of `preparerDefaults` that fills fields the project has left
 * blank. Returns an empty object when there is nothing to fill, so callers can
 * skip a no-op state update. Never overwrites a value the user has typed —
 * clearing a field on purpose survives, because the backfill runs once per
 * project rather than on every render.
 */
export function blankPreparerDefaults(
  project: Partial<Project>,
  profile: CompanyProfile | null,
  createdAt?: string | null,
): Partial<Project> {
  const defaults = preparerDefaults(profile, createdAt);
  const updates: Partial<Project> = {};
  for (const [key, value] of Object.entries(defaults) as [keyof Project, string][]) {
    if (!value) continue;
    const current = project[key];
    if (typeof current === 'string' && current.trim()) continue;
    if (current != null && typeof current !== 'string') continue;
    (updates as Record<string, string>)[key] = value;
  }
  return updates;
}
